import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ChevronDown, ChevronRight, User, Mail, Phone, MapPin, Shield, AlertCircle, FileText, KeyRound, Flag } from 'lucide-react';

/**
 * Customer form — operator-facing.
 *
 * Surfaces every field that exists on the Customer schema so the operator
 * can edit anything a tenant skipped at signup. Organized into accordions
 * with explicit Required/Optional labels:
 *
 *   1. Required Information (always open) — name, email, phone, address
 *   2. Personal Details (collapsed) — DOB, drivers license
 *   3. Emergency / Additional Access (collapsed) — additional access
 *      person + ICE / next-of-kin contact
 *   4. Military / SCRA (collapsed) — branch, active duty, reserves
 *
 * Mirror-pair of the public Rent Wizard's Step 1: the tenant sees only
 * Required by default with an "Additional Details (optional)" accordion;
 * the operator sees Everything, organized.
 *
 * Vehicle/Boat/Trailer arrays live in the separate "Assets & Vehicles"
 * tab in CustomerForm.js so this section stays focused on the human.
 */

const AccordionSection = ({ title, subtitle, icon: Icon, defaultOpen = false, required = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 text-left"
            >
                {Icon && <Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />}
                <div className="flex-1">
                    <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
                        {title}
                        {required ? (
                            <span className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider">Required</span>
                        ) : (
                            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Optional</span>
                        )}
                    </h3>
                    {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
                {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            </button>
            {open && (
                <div className="border-t border-slate-100 px-5 py-4">
                    {children}
                </div>
            )}
        </div>
    );
};

const InputField = ({ label, name, register, errors, icon: Icon, type = "text", placeholder, required = false }) => (
    <div>
        <label htmlFor={name} className="block text-[12px] font-medium text-slate-700 mb-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <div className="relative">
            {Icon && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon className="h-4 w-4 text-slate-400" />
                </div>
            )}
            <input
                type={type}
                id={name}
                {...register(name)}
                placeholder={placeholder}
                className={`${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 block w-full text-[13px] border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors?.[name] ? 'border-rose-400' : ''}`}
            />
        </div>
        {errors?.[name] && (
            <p className="mt-1 text-[11px] text-rose-600">{errors[name].message}</p>
        )}
    </div>
);

const SelectField = ({ label, name, register, options }) => (
    <div>
        <label htmlFor={name} className="block text-[12px] font-medium text-slate-700 mb-1">{label}</label>
        <select
            id={name}
            {...register(name)}
            className="block w-full text-[13px] border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
            {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
            ))}
        </select>
    </div>
);

const PersonalSection = () => {
    const { register, watch, setValue, formState: { errors } } = useFormContext();
    const isMilitary = watch('military_info.is_military');

    // Auto-derive full_name from first_name + last_name whenever either
    // changes. We keep the Full Name input visible (operator may want to
    // override it for business customers with a trade name), but the
    // common path is split fields drive the canonical string. The PTI
    // bridge reads first_name / last_name directly and only falls back
    // to full_name parsing for pre-split legacy records.
    const firstName = watch('first_name');
    const lastName = watch('last_name');
    React.useEffect(() => {
        const first = (firstName || '').trim();
        const last = (lastName || '').trim();
        if (first && last) {
            setValue('full_name', `${first} ${last}`, { shouldDirty: true, shouldValidate: false });
        }
    }, [firstName, lastName, setValue]);

    return (
        <div>
            {/* ===== 1. REQUIRED INFORMATION (always open) ===== */}
            <AccordionSection
                title="Required Information"
                subtitle="Minimum information needed to rent. Tenant filled these at signup."
                icon={User}
                defaultOpen={true}
                required={true}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Split name fields drive the canonical record. The PTI
                        bridge reads these directly (no naive whitespace
                        parsing → handles "Van Houten", "De La Cruz", etc.). */}
                    <InputField label="First Name" name="first_name" register={register} errors={errors}
                        icon={User} placeholder="John" required />
                    <InputField label="Last Name" name="last_name" register={register} errors={errors}
                        icon={User} placeholder="Doe" required />
                    <div className="sm:col-span-2">
                        <InputField label="Full Name" name="full_name" register={register} errors={errors}
                            icon={User} placeholder="John Doe" required />
                    </div>
                    <InputField label="Email" name="email" register={register} errors={errors}
                        type="email" icon={Mail} placeholder="john@example.com" required />
                    <InputField label="Phone" name="phone" register={register} errors={errors}
                        type="tel" icon={Phone} placeholder="(555) 123-4567" required />
                    <div className="sm:col-span-2">
                        <InputField label="Street Address" name="address" register={register} errors={errors}
                            icon={MapPin} required />
                    </div>
                    <InputField label="City" name="city" register={register} errors={errors} required />
                    <div className="grid grid-cols-2 gap-3">
                        <InputField label="State" name="state" register={register} errors={errors} required />
                        <InputField label="ZIP Code" name="zip_code" register={register} errors={errors} required />
                    </div>
                </div>
            </AccordionSection>

            {/* ===== 1.5 GATE ACCESS — kept close to Required because operators
                check/edit this most after contact details. PTI-style numeric
                PIN; same code applies across ALL units this customer rents
                (matches Falcon XT mental model: code = person, not unit). =====*/}
            <AccordionSection
                title="Gate Access"
                subtitle="Customer's gate code (PIN). Same code applies across all their units."
                icon={KeyRound}
                defaultOpen={true}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InputField
                        label="Gate Code"
                        name="gate_code"
                        register={register}
                        errors={errors}
                        icon={KeyRound}
                        placeholder="e.g. 4521"
                    />
                </div>
            </AccordionSection>

            {/* ===== 2. PERSONAL DETAILS (optional) ===== */}
            <AccordionSection
                title="Personal Details"
                subtitle="Date of birth, driver's license. Often needed for lien/eviction process."
                icon={FileText}
                defaultOpen={false}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InputField label="Date of Birth" name="date_of_birth" register={register} errors={errors}
                        type="date" />
                    <InputField label="Middle Name" name="middle_name" register={register} errors={errors} />
                    <InputField label="Driver's License Number" name="drivers_license_number" register={register} errors={errors} />
                    <InputField label="Driver's License State" name="drivers_license_state" register={register} errors={errors}
                        placeholder="TX" />
                    <InputField label="Driver's License Expiration" name="drivers_license_expiry" register={register} errors={errors}
                        type="date" />
                    <InputField label="SSN Last 4 (optional)" name="ssn_last_4" register={register} errors={errors}
                        placeholder="****" />
                </div>
            </AccordionSection>

            {/* ===== 3. EMERGENCY / ADDITIONAL ACCESS (optional) ===== */}
            <AccordionSection
                title="Emergency Contact & Additional Access"
                subtitle="Person to contact in case of emergency or to authorize unit access (e.g., in case of tenant absence or death)."
                icon={AlertCircle}
                defaultOpen={false}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <InputField label="Emergency Contact Name" name="emergency_contact.name" register={register} errors={errors} />
                    <InputField label="Emergency Contact Phone" name="emergency_contact.phone" register={register} errors={errors}
                        type="tel" />
                    <InputField label="Emergency Contact Relationship" name="emergency_contact.relationship" register={register} errors={errors}
                        placeholder="Spouse, Sibling, etc." />
                </div>
                <div className="border-t border-slate-100 pt-4">
                    <p className="text-[11px] text-slate-500 mb-2">
                        Additional Access Person — authorized to receive space number, access code,
                        account status, or assistance with lock removal.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <InputField label="Access Person Name" name="additional_access_person_name" register={register} errors={errors} />
                        <InputField label="Access Person Phone" name="additional_access_person_phone" register={register} errors={errors}
                            type="tel" />
                    </div>
                </div>
            </AccordionSection>

            {/* ===== 4. MILITARY / SCRA (optional) ===== */}
            <AccordionSection
                title="Military / SCRA"
                subtitle="Servicemembers Civil Relief Act disclosure. Defaults to non-military."
                icon={Shield}
                defaultOpen={false}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <label className="flex items-center gap-2 text-[13px] text-slate-700">
                            <input type="checkbox" {...register('military_info.is_military')}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                            <span>Currently in the U.S. military</span>
                        </label>
                    </div>
                    {isMilitary && (
                        <>
                            <SelectField label="Branch" name="military_info.branch" register={register}
                                options={['', 'Army', 'Navy', 'Air Force', 'Marines', 'Coast Guard', 'Space Force']} />
                            <SelectField label="Active Duty" name="military_info.active_duty" register={register}
                                options={['', 'Yes', 'No']} />
                            <SelectField label="Reserves / National Guard" name="military_info.reserves_or_guard" register={register}
                                options={['', 'Yes', 'No']} />
                            <InputField label="Service Member Name (if different)" name="military_info.servicemember_name" register={register} errors={errors} />
                        </>
                    )}
                </div>
            </AccordionSection>

            {/* ===== 5. CONTACT PREFERENCES (optional) ===== */}
            <AccordionSection
                title="Contact Preferences"
                subtitle="Alternate phones, preferred contact method, secondary email."
                icon={Phone}
                defaultOpen={false}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InputField label="Alternate Phone" name="alternate_phone" register={register} errors={errors}
                        type="tel" />
                    <InputField label="Work Phone" name="work_phone" register={register} errors={errors}
                        type="tel" />
                    <InputField label="Secondary Email" name="email_secondary" register={register} errors={errors}
                        type="email" />
                    <SelectField label="Preferred Contact Method" name="preferred_contact_method" register={register}
                        options={['email', 'sms']} />
                </div>
            </AccordionSection>

            {/* ===== 6. ACCOUNT FLAGS (optional) =====
                Operator-managed exemption switches. Surface here so a
                manager can mark a customer as Late-Fee Exempt etc. and
                have the Customers list dropdown bucket them correctly.
                Downstream enforcement (late-fee engine skip, invoice tax
                skip, suspend-on-delinquency bypass) lives in those
                respective engines — these are just the source-of-truth
                flags they read from. */}
            <AccordionSection
                title="Account Flags"
                subtitle="Operator exemptions for late fees, tax, and automatic lockout."
                icon={Flag}
                defaultOpen={false}
            >
                <div className="space-y-3">
                    <label className="flex items-start gap-3 text-[13px] text-slate-700">
                        <input
                            type="checkbox"
                            {...register('late_fee_exempt')}
                            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Late Fee Exempt</div>
                            <div className="text-[12px] text-slate-500">
                                Skip late fees on this customer&rsquo;s rentals.
                            </div>
                        </div>
                    </label>
                    <label className="flex items-start gap-3 text-[13px] text-slate-700">
                        <input
                            type="checkbox"
                            {...register('tax_exempt')}
                            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Tax Exempt</div>
                            <div className="text-[12px] text-slate-500">
                                Do not charge sales tax on retail line items.
                            </div>
                        </div>
                    </label>
                    <label className="flex items-start gap-3 text-[13px] text-slate-700">
                        <input
                            type="checkbox"
                            {...register('auto_lockout_disabled')}
                            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Automatic Lockout Disabled</div>
                            <div className="text-[12px] text-slate-500">
                                Never auto-suspend gate access on delinquency.
                            </div>
                        </div>
                    </label>
                    <label className="flex items-start gap-3 text-[13px] text-slate-700">
                        <input
                            type="checkbox"
                            {...register('marketing_opt_in')}
                            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900">Marketing Opt-In</div>
                            <div className="text-[12px] text-slate-500">
                                Include in re-engagement campaigns (auto-set to true on signup; flip off if customer requests removal).
                            </div>
                        </div>
                    </label>
                </div>
            </AccordionSection>
        </div>
    );
};

export default PersonalSection;
